import React, { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { Eye, EyeOff, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuthStore } from '@/store/authStore'
import { taskService } from '@/services/taskService'
import api from '@/services/api'

const userName = (u) => (u.employee ? `${u.employee.first_name} ${u.employee.last_name || ''}`.trim() : u.email)

// Watch / stop watching a task, and see or change who watches it. Watchers get the task's
// comments and status changes in the bell (and by email, per their notification settings).
const TaskWatchButton = ({ projectId, taskId, className }) => {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const key = ['task-watchers', String(taskId)]

  const { data } = useQuery(key, () => taskService.getWatchers(projectId, taskId), { enabled: !!taskId })
  const { data: usersData } = useQuery(
    'team-members',
    async () => { const r = await api.get('/users/team-members'); return r.data },
    { enabled: open }
  )
  const info = data?.data || { watchers: [], count: 0, watching: false }
  const watcherIds = new Set(info.watchers.map(w => w.id))
  const others = (usersData?.data?.users || []).filter(u => !watcherIds.has(u.id))
  const canRemoveOthers = user?.role === 'admin'

  const run = async (action, success) => {
    setBusy(true)
    try {
      const res = await action()
      queryClient.setQueryData(key, res)
      if (success) toast.success(success)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not update watchers')
    } finally {
      setBusy(false)
    }
  }

  const toggleSelf = () => run(
    () => (info.watching ? taskService.removeWatcher(projectId, taskId, user.id) : taskService.addWatcher(projectId, taskId)),
    info.watching ? 'You stopped watching this task' : "You're watching this task"
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={className || 'h-7 gap-1.5 px-2 text-muted-foreground'}
          title={info.watching ? 'You are watching this task' : 'Watch this task'}
        >
          {info.watching ? <Eye className="h-4 w-4 text-primary" /> : <EyeOff className="h-4 w-4" />}
          <span className="text-xs">{info.watching ? 'Watching' : 'Watch'}</span>
          {info.count > 0 && <span className="text-xs tabular-nums">· {info.count}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="end">
        <div className="p-3 border-b space-y-2">
          <p className="text-xs text-muted-foreground">Watchers get this task's comments and status changes.</p>
          <Button size="sm" variant={info.watching ? 'outline' : 'default'} className="w-full h-8" disabled={busy} onClick={toggleSelf}>
            {info.watching ? 'Stop watching' : 'Start watching'}
          </Button>
        </div>
        <div className="max-h-56 overflow-y-auto py-1">
          {info.watchers.length === 0 && <p className="px-3 py-2 text-xs text-muted-foreground">Nobody is watching yet.</p>}
          {info.watchers.map(w => (
            <div key={w.id} className="flex items-center gap-2 px-3 py-1.5 text-sm">
              <Avatar className="h-6 w-6">
                <AvatarFallback className="text-[10px] bg-primary/10 text-primary">{(w.name || '?').charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>
              <span className="flex-1 truncate">{w.name}{w.id === user?.id && <span className="text-muted-foreground"> (you)</span>}</span>
              {(w.id === user?.id || canRemoveOthers) && (
                <button
                  type="button"
                  className="text-muted-foreground hover:text-destructive"
                  title={w.id === user?.id ? 'Stop watching' : `Remove ${w.name}`}
                  disabled={busy}
                  onClick={() => run(() => taskService.removeWatcher(projectId, taskId, w.id))}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="p-2 border-t">
          <Select value="" onValueChange={(id) => run(() => taskService.addWatcher(projectId, taskId, Number(id)), 'Watcher added')}>
            <SelectTrigger className="h-8 text-xs text-muted-foreground" aria-label="Add a watcher">
              <SelectValue placeholder="Add a watcher…" />
            </SelectTrigger>
            <SelectContent>
              {others.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">Everyone is already watching</div>}
              {others.map(u => <SelectItem key={u.id} value={String(u.id)}>{userName(u)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default TaskWatchButton
